import { AccessTimeRounded } from "@mui/icons-material";
import { MenuItem, OutlinedInput, Select, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";

import { timezoneOptions } from "@/constants";
import { updateUser as saveUser } from "@/services";
import { dispatch, updateUser, useSelector } from "@/store";
import { handleError } from "@/utils";

// Current time at the given GMT offset (hours), formatted HH:MM:SS
const formatTime = (offsetHours: number): string => {
  const now = new Date();
  const utcMs = now.getTime() + now.getTimezoneOffset() * 60000;
  const target = new Date(utcMs + offsetHours * 3600000);

  return target.toLocaleTimeString("en-GB", { hour12: false });
};

interface Props {
  // When true the component stretches to fill its container and the timezone
  // select flexes to consume the remaining row. Used in the mobile drawer
  // where the row needs to span the full width.
  fullWidth?: boolean;
}

const HeaderClock = ({ fullWidth = false }: Props) => {
  const { user } = useSelector((state) => state.auth);

  const [timezone, setTimezone] = useState<string>(user?.timezone ?? "0");
  const [, setTick] = useState<number>(0);

  // Keep local timezone in sync if the stored user changes (e.g. re-login)
  useEffect(() => {
    setTimezone(user?.timezone ?? "0");
  }, [user?.timezone]);

  useEffect(() => {
    const timer = setInterval(() => setTick((value) => value + 1), 1000);

    return () => clearInterval(timer);
  }, []);

  const handleChange = async (value: string) => {
    setTimezone(value);
    if (!user) return;
    dispatch(updateUser({ ...user, timezone: value }));
    try {
      await saveUser(user._id, { timezone: value });
    } catch (error) {
      handleError(error);
    }
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={1}
      sx={fullWidth ? { width: "100%", minWidth: 0 } : undefined}
    >
      <AccessTimeRounded fontSize="small" sx={{ color: "text.secondary" }} />
      <Typography
        variant="body2"
        sx={{ fontVariantNumeric: "tabular-nums", minWidth: 62, fontWeight: 600 }}
      >
        {formatTime(Number(timezone || 0))}
      </Typography>
      <Select
        size="small"
        value={String(timezone)}
        input={<OutlinedInput />}
        onChange={(event) => handleChange(event.target.value)}
        MenuProps={{ PaperProps: { sx: { maxHeight: 5 * 48 } } }}
        sx={fullWidth ? { flex: 1, minWidth: 0 } : { minWidth: 150 }}
      >
        {timezoneOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </Stack>
  );
};

export default HeaderClock;
